import { ScrollText } from "lucide-react"
import { useEffect, useMemo, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { hasSkillSlashToken } from "@/components/agent/skill-slash-token"
import { useDropdownMenu } from "@/components/ui/dropdown-menu-position"
import { Button } from "@/components/ui/primitives"
import { useTranslation } from "@/hooks/use-translation"
import type { AgentSessionSkill } from "@/lib/agent-api"
import { cn } from "@/lib/utils"

type PickerProps = {
  availableSkills: AgentSessionSkill[]
  /** Composer text — skills already present as `/slug` are not pickable. */
  composerText?: string
  /** Prefer live editor text when opening (TipTap may be ahead of React state). */
  getComposerText?: () => string
  disabled?: boolean
  onSelect: (skill: AgentSessionSkill) => void
}

function skillBlockedInComposer(skill: AgentSessionSkill, text: string): boolean {
  return hasSkillSlashToken(text, skill.slug)
}

export function AgentSessionSkillsPicker({
  availableSkills,
  composerText = "",
  getComposerText,
  disabled = false,
  onSelect,
}: PickerProps) {
  const { t } = useTranslation("workspace")
  const [open, setOpen] = useState(false)
  const [textSnapshot, setTextSnapshot] = useState(composerText)
  const [hoveredId, setHoveredId] = useState<number | null>(null)
  const rootRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const menuRef = useRef<HTMLUListElement>(null)

  useEffect(() => {
    if (!open) setTextSnapshot(composerText)
  }, [composerText, open])

  const pickableSkills = useMemo(
    () => availableSkills.filter((skill) => !skillBlockedInComposer(skill, textSnapshot)),
    [availableSkills, textSnapshot],
  )
  const nothingToPick = useMemo(() => {
    if (availableSkills.length === 0) return true
    const live = getComposerText?.() ?? composerText
    return availableSkills.every((skill) => skillBlockedInComposer(skill, live))
  }, [availableSkills, composerText, getComposerText])

  const { menuStyle, portalRoot } = useDropdownMenu({
    open,
    direction: "up",
    align: "left",
    anchorRef: buttonRef,
    menuRef,
    rootRef,
    resolveWidth: () => ({ minWidth: "14rem", width: "16rem", maxWidth: "16rem" }),
    onOutsideClick: () => setOpen(false),
  })

  useEffect(() => {
    if (!open) return
    function handleKey(e: globalThis.KeyboardEvent) {
      if (e.key === "Escape") setOpen(false)
    }
    document.addEventListener("keydown", handleKey, true)
    return () => document.removeEventListener("keydown", handleKey, true)
  }, [open])

  useEffect(() => {
    if (nothingToPick && open) setOpen(false)
  }, [nothingToPick, open])

  useEffect(() => {
    if (!open) setHoveredId(null)
  }, [open])

  return (
    <div ref={rootRef} className="relative shrink-0">
      <Button
        ref={buttonRef}
        type="button"
        size="sm"
        variant="ghost"
        className="h-8 w-8 shrink-0 p-0"
        disabled={disabled || nothingToPick}
        aria-label={t("agent.skillsPicker")}
        aria-expanded={open}
        onClick={() => {
          const live = getComposerText?.() ?? composerText
          setTextSnapshot(live)
          setOpen((v) => !v)
        }}
      >
        <ScrollText className="h-4 w-4" aria-hidden />
      </Button>

      {open && menuStyle && portalRoot
        ? createPortal(
            <ul
              ref={menuRef}
              role="listbox"
              data-agent-portal-menu
              style={menuStyle}
              className="it2tv-scrollbar z-[240] max-h-64 w-64 overflow-x-hidden overflow-y-auto rounded-lg border border-ui-border bg-ui-bg-elevated p-1 shadow-lg"
            >
              {pickableSkills.length === 0 ? (
                <li className="px-2 py-2 text-[11px] text-ui-text-muted">{t("agent.skillsEmpty")}</li>
              ) : (
                pickableSkills.map((skill) => {
                  const hovered = hoveredId === skill.id
                  return (
                    <li key={skill.id} className="min-w-0">
                      <button
                        type="button"
                        role="option"
                        aria-selected={hovered}
                        disabled={disabled}
                        className={cn(
                          "flex w-full max-w-full min-w-0 flex-col items-start gap-0.5 overflow-hidden rounded-lg border-0 bg-transparent px-2 py-1.5 text-left text-xs shadow-none transition-colors",
                          hovered
                            ? "bg-ui-accent/10 text-ui-text-primary"
                            : "text-ui-text-primary hover:bg-ui-surface-raised",
                          "disabled:opacity-50",
                        )}
                        onMouseEnter={() => setHoveredId(skill.id)}
                        onMouseLeave={() => setHoveredId((id) => (id === skill.id ? null : id))}
                        onClick={() => {
                          onSelect(skill)
                          setOpen(false)
                        }}
                      >
                        <span className="block w-full min-w-0 truncate font-medium">{skill.name}</span>
                        {skill.description ? (
                          <span className="line-clamp-2 w-full min-w-0 text-[10px] break-words text-ui-text-muted">
                            {skill.description}
                          </span>
                        ) : null}
                        <span className="block w-full min-w-0 truncate font-mono text-[11px] text-ui-link">
                          /{skill.slug}
                        </span>
                      </button>
                    </li>
                  )
                })
              )}
            </ul>,
            portalRoot,
          )
        : null}
    </div>
  )
}
